import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from 'react';
import {addToast} from '@albert-io/atomic';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

import {
  useThemePreferenceQuery,
  useUpdateColorModePreferenceMutation
} from './Theme.queries.generated';
import type {Theme, ThemePreference} from './themeStorage';
import {
  isThemePreference,
  readStoredThemePreference,
  writeStoredThemePreference
} from './themeStorage';

export type {Theme, ThemePreference} from './themeStorage';

const DARK_MODE_FLAG = 'color_mode_dark';
const DARK_MODE_CLASS = 'dark';
const THEME_TRANSITION_CLASS = 'theme-transition';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const showSaveFailedToast = () =>
  addToast({
    color: 'negative',
    title: 'Error',
    message: 'There was a problem updating your appearance.'
  });

interface ThemeContextProps {
  theme: Theme;
  themePreference: ThemePreference;
  isDarkMode: boolean;
  /** Applies the color mode right away; resolves `false` if it could not be saved. */
  setThemePreference: (preference: ThemePreference) => Promise<boolean>;
  isFeatureEnabled: boolean;
  isLoading: boolean;
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: 'light',
  themePreference: 'system',
  isDarkMode: false,
  setThemePreference: () => Promise.resolve(false),
  isFeatureEnabled: false,
  isLoading: true
});

export const ThemeConsumer = ThemeContext.Consumer;

const subscribeToSystemTheme = (onChange: () => void) => {
  const mediaQueryList = window.matchMedia(SYSTEM_DARK_QUERY);
  mediaQueryList.addEventListener('change', onChange);
  return () => {
    mediaQueryList.removeEventListener('change', onChange);
  };
};

const getSystemTheme = (): Theme =>
  window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light';

/** The device's own color scheme, whichever color mode the user picked. */
export const useSystemTheme = (): Theme =>
  useSyncExternalStore(subscribeToSystemTheme, getSystemTheme);

const useThemeContextValue = ({
  themePreference,
  isFeatureEnabled,
  isLoading,
  setThemePreference
}: Omit<ThemeContextProps, 'theme' | 'isDarkMode'>): ThemeContextProps => {
  const systemTheme = useSystemTheme();
  const theme: Theme = themePreference === 'system' ? systemTheme : themePreference;
  const isDarkMode = isFeatureEnabled && theme === 'dark';

  useEffect(() => {
    const root = document.documentElement;
    if (root.classList.contains(DARK_MODE_CLASS) === isDarkMode) return;

    if (
      typeof document.startViewTransition !== 'function' ||
      window.matchMedia(REDUCED_MOTION_QUERY).matches
    ) {
      root.classList.toggle(DARK_MODE_CLASS, isDarkMode);
      return;
    }

    let isCurrent = true;
    root.classList.add(THEME_TRANSITION_CLASS);
    // A skipped transition still runs its update, so a newer switch must win over it.
    const transition = document.startViewTransition(() => {
      if (isCurrent) root.classList.toggle(DARK_MODE_CLASS, isDarkMode);
    });
    // skipTransition() rejects `ready`, which nothing awaits; unhandled, it reports as a page error.
    transition.ready.catch(() => {});
    transition.finished.finally(() => {
      if (isCurrent) root.classList.remove(THEME_TRANSITION_CLASS);
    });
    return () => {
      isCurrent = false;
      transition.skipTransition();
      root.classList.remove(THEME_TRANSITION_CLASS);
    };
  }, [isDarkMode]);

  return useMemo(
    () => ({
      theme,
      themePreference,
      isDarkMode,
      setThemePreference,
      isFeatureEnabled,
      isLoading
    }),
    [theme, themePreference, isDarkMode, setThemePreference, isFeatureEnabled, isLoading]
  );
};

interface AccountThemeProviderProps extends PropsWithChildrenRequired {
  hasSession: boolean;
}

interface ThemeAnswer {
  isFeatureEnabled: boolean;
  themePreference: ThemePreference;
}

/**
 * The account's color mode behind `color_mode_dark`, or this device's copy until the server answers.
 * Signed out, the page then follows the device setting, but only once the flag is on for everyone.
 */
const AccountThemeProvider = ({children, hasSession}: AccountThemeProviderProps) => {
  const {data, loading} = useThemePreferenceQuery({
    skip: !hasSession,
    variables: {flag: DARK_MODE_FLAG}
  });
  const signedOutFlag = useIsFeatureFlagEnabled(DARK_MODE_FLAG, {skip: hasSession});
  const [updateColorModePreference] = useUpdateColorModePreferenceMutation();
  const storedPreference = readStoredThemePreference();

  const user = data?.currentUser;
  const accountSavedTheme: ThemeAnswer | undefined = data && {
    isFeatureEnabled: Boolean(data.featureFlag?.enabled),
    themePreference: isThemePreference(user?.colorModePreference)
      ? user.colorModePreference
      : 'system'
  };
  const signedOutTheme: ThemeAnswer | undefined = signedOutFlag.isLoading
    ? undefined
    : {isFeatureEnabled: signedOutFlag.isFeatureEnabled, themePreference: 'system'};
  // Until the server answers, keep this device's copy: the head script already painted it.
  const {isFeatureEnabled, themePreference} = (hasSession ? accountSavedTheme : signedOutTheme) ?? {
    isFeatureEnabled: storedPreference !== null,
    themePreference: storedPreference ?? 'system'
  };
  const isLoading = hasSession ? loading : signedOutFlag.isLoading;
  const deviceTheme = isFeatureEnabled ? themePreference : null;

  useEffect(() => {
    writeStoredThemePreference(deviceTheme);
  }, [deviceTheme]);

  const userId = user?.id;
  const setThemePreference = useCallback(
    async (preference: ThemePreference) => {
      if (!isFeatureEnabled) return false;
      if (!userId) {
        showSaveFailedToast();
        return false;
      }

      const saved = await updateColorModePreference({
        variables: {id: userId, colorModePreference: preference},
        optimisticResponse: {
          updateUser: {
            __typename: 'UpdateUserResult',
            result: {__typename: 'User', id: userId, colorModePreference: preference}
          }
        }
      })
        .then((response) => Boolean(response.data?.updateUser.result))
        .catch(() => false);

      if (!saved) {
        showSaveFailedToast();
      }
      return saved;
    },
    [isFeatureEnabled, userId, updateColorModePreference]
  );

  const value = useThemeContextValue({
    themePreference,
    isFeatureEnabled,
    isLoading,
    setThemePreference
  });

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

interface OverriddenThemeProviderProps extends PropsWithChildrenRequired {
  isFeatureEnabled: boolean;
}

const OverriddenThemeProvider = ({children, isFeatureEnabled}: OverriddenThemeProviderProps) => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() =>
    isFeatureEnabled ? (readStoredThemePreference() ?? 'system') : 'system'
  );

  const setThemePreference = useCallback(
    (preference: ThemePreference) => {
      if (!isFeatureEnabled) return Promise.resolve(false);

      writeStoredThemePreference(preference);
      setThemePreferenceState(preference);
      return Promise.resolve(true);
    },
    [isFeatureEnabled]
  );

  const value = useThemeContextValue({
    themePreference,
    isFeatureEnabled,
    isLoading: false,
    setThemePreference
  });

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

interface ThemeProviderProps extends PropsWithChildrenRequired {
  /** Whether someone is signed in. Signed out, the app follows the device setting once dark mode is on for everyone. */
  hasSession?: boolean;
  /**
   * Skips the account and the `color_mode_dark` flag and uses this value. Only Storybook sets it,
   * since it has no backend or Apollo client to check them against.
   */
  featureEnabledOverride?: boolean;
}

export const ThemeProvider = ({
  children,
  hasSession = false,
  featureEnabledOverride
}: ThemeProviderProps) =>
  featureEnabledOverride === undefined ? (
    <AccountThemeProvider hasSession={hasSession}>{children}</AccountThemeProvider>
  ) : (
    <OverriddenThemeProvider isFeatureEnabled={featureEnabledOverride}>
      {children}
    </OverriddenThemeProvider>
  );
